/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { Box, SvgIcon, Tooltip } from "@material-ui/core";
import { PCView, SPView } from "components/common/MediaQuery";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";
import { useSelector } from "store";
import { colorTokens } from "theme/theme";

import { isSmartPhone } from "../../utility";
import { useFullscreen } from "./useFullscreen";

import type { GiftItemData } from "modules/streaming";
import type { FC } from "react";

/**
 * コメントエリア下のコメント送信エリア
 * NOTE: ログインしていない場合はコメントは投稿できない
 * @see https://www.notion.so/balusco/cf127373c94744c495144820c6f40ec8?pvs=4#38f7b6c4b11a453293efe7c6316eeea8
 */
export const StreamingSendCommentV2: React.FC<{
  isDarkModeEnabled: boolean;
  isOpenGiftDrawer: boolean;
  isSelectedGift: boolean;
  canPurchaseGiftItem: boolean;
  userIcon: string | undefined;
  userName: string;
  inputRef: React.RefObject<HTMLInputElement>;
  comment: string;
  canSendComment: boolean;
  giftItems: GiftItemData[];
  onClickSendComment: () => void;
  setComment: (comment: string) => void;
  onClickOpenGiftDrawer: () => void;
  onClickOpenPawChargeModal: () => void;
}> = ({
  isDarkModeEnabled,
  isOpenGiftDrawer,
  isSelectedGift,
  canPurchaseGiftItem,
  userIcon,
  userName,
  inputRef,
  comment,
  canSendComment,
  giftItems,
  onClickSendComment,
  setComment,
  onClickOpenGiftDrawer,
  onClickOpenPawChargeModal,
}) => {
  const { t } = useI18n();
  const isCommentInputDisabled = useSelector(
    (state) => state.streaming.isCommentDisabled
  );
  const { isFullscreen, handleExitFullscreen } = useFullscreen();

  const enterPressed = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onClickSendComment();
    }
  };

  // @ts-expect-error TS7006
  const onFocusCommentInput = (_) => {
    if (isSmartPhone()) {
      window.setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 500);
    }
  };

  const onClickOpenPawCharge = async () => {
    if (isFullscreen) {
      await handleExitFullscreen();
    }
    onClickOpenPawChargeModal();
  };

  // コメント入力フォーム
  return (
    <Box
      display={"grid"}
      gridTemplateColumns={"max-content 1fr max-content"}
      alignItems={"center"}
      gridGap={"4px"}
    >
      {/* ユーザーアイコンを表示 */}
      <img
        src={userIcon}
        alt={userName}
        className="icon"
        css={css`
          display: block;
          width: 24px;
          height: 24px;
          background-color: #fff;
          border-radius: 50%;
          aspect-ratio: 1 / 1;
          object-fit: cover;
        `}
      />

      <Tooltip
        title={
          isCommentInputDisabled ? t("streaming.comment.catchUpToComment") : ""
        }
        arrow
        placement="top"
        enterTouchDelay={100}
        leaveTouchDelay={3000}
      >
        <input
          type={"text"}
          name={"comment"}
          ref={inputRef}
          maxLength={50}
          autoComplete={"off"}
          placeholder={t("streaming.comment.placeholder")}
          disabled={isCommentInputDisabled}
          onKeyPress={enterPressed}
          onChange={(e) => setComment(e.currentTarget.value)}
          onFocus={() => {
            console.log("comment onFocus");
            onFocusCommentInput(true);
          }}
          onBlur={() => {
            console.log("comment onBlur");
            onFocusCommentInput(false);
          }}
          css={css`
            width: 100%;
            padding: 7px 12px !important;
            caret-color: #bbbbbb;
            color: ${isDarkModeEnabled
              ? colorTokens.colors["text.primary"].dark
              : colorTokens.colors["text.primary"].default};
            font-size: 16px !important;
            background-color: inherit !important;
            border: ${isDarkModeEnabled
              ? `1px solid rgba(255, 255, 255, 0.1) !important`
              : `1px solid ${colorTokens.colors["border.tertiary"].default} !important`};
            border-radius: 8px !important;
            &::placeholder {
              color: #bbbbbb !important;
            }
          `}
        />
      </Tooltip>

      <PCView>
        <StreamingSendCommentButtonRender
          isDarkModeEnabled={isDarkModeEnabled}
          isInputComment={comment.length > 0}
          isExistGiftItems={giftItems.length !== 0}
          isSelectedGift={isSelectedGift}
          canPurchaseGiftItem={canPurchaseGiftItem}
          canSendComment={canSendComment}
          onClickSendComment={onClickSendComment}
          onClickOpenGiftDrawer={onClickOpenGiftDrawer}
          onClickOpenPawCharge={onClickOpenPawCharge}
        />
      </PCView>

      <SPView>
        {!isOpenGiftDrawer && (
          <StreamingSendCommentButtonRender
            isDarkModeEnabled={isDarkModeEnabled}
            isInputComment={comment.length > 0}
            isExistGiftItems={giftItems.length !== 0}
            isSelectedGift={isSelectedGift}
            canPurchaseGiftItem={canPurchaseGiftItem}
            canSendComment={canSendComment}
            onClickSendComment={onClickSendComment}
            onClickOpenGiftDrawer={onClickOpenGiftDrawer}
            onClickOpenPawCharge={onClickOpenPawCharge}
          />
        )}
      </SPView>
    </Box>
  );
};

const StreamingSendCommentButtonRender: FC<{
  isDarkModeEnabled: boolean;
  isInputComment: boolean;
  isExistGiftItems: boolean;
  isSelectedGift: boolean;
  canPurchaseGiftItem: boolean;
  canSendComment: boolean;
  onClickSendComment: () => void;
  onClickOpenGiftDrawer: () => void;
  onClickOpenPawCharge: () => Promise<void>;
}> = ({
  isDarkModeEnabled,
  isInputComment,
  isExistGiftItems,
  isSelectedGift,
  canPurchaseGiftItem,
  canSendComment,
  onClickSendComment,
  onClickOpenGiftDrawer,
  onClickOpenPawCharge,
}) => {
  const { t } = useI18n();

  if (isSelectedGift) {
    if (canPurchaseGiftItem) {
      return (
        <StreamingSendCommentButton
          color={
            isDarkModeEnabled
              ? colorTokens.colors["icon.secondary"].dark
              : colorTokens.colors["icon.secondary"].default
          }
          bgcolor={
            isDarkModeEnabled
              ? colorTokens.colors["action.primary"].dark
              : colorTokens.colors["action.primary"].default
          }
          border={
            isDarkModeEnabled
              ? colorTokens.colors["border.primary"].dark
              : colorTokens.colors["border.primary"].default
          }
          disabled={!canSendComment}
          onClick={onClickSendComment}
        >
          {t("streaming.gift.purchaseAndSend")}
        </StreamingSendCommentButton>
      );
    } else {
      return (
        <StreamingSendCommentButton
          color={
            isDarkModeEnabled
              ? colorTokens.colors["icon.secondary"].dark
              : colorTokens.colors["icon.secondary"].default
          }
          bgcolor={
            isDarkModeEnabled
              ? colorTokens.colors["action.primary"].dark
              : colorTokens.colors["action.primary"].default
          }
          border={
            isDarkModeEnabled
              ? colorTokens.colors["border.primary"].dark
              : colorTokens.colors["border.primary"].default
          }
          onClick={onClickOpenPawCharge}
        >
          {t("streaming.gift.PAWCharge")}
        </StreamingSendCommentButton>
      );
    }
  } else {
    if (isInputComment) {
      return (
        <StreamingSendCommentButton
          color={
            isDarkModeEnabled
              ? colorTokens.colors["icon.secondary"].dark
              : colorTokens.colors["icon.secondary"].default
          }
          bgcolor={
            isDarkModeEnabled
              ? colorTokens.colors["action.primary"].dark
              : colorTokens.colors["action.primary"].default
          }
          border={
            isDarkModeEnabled
              ? colorTokens.colors["border.primary"].dark
              : colorTokens.colors["border.primary"].default
          }
          disabled={!canSendComment}
          onClick={onClickSendComment}
        >
          <SendIcon />
        </StreamingSendCommentButton>
      );
    } else {
      return (
        <Box>
          {/* ギフトボタン */}
          {isExistGiftItems && (
            <StreamingSendCommentButton
              color={
                isDarkModeEnabled
                  ? "#ffffff"
                  : colorTokens.colors["icon.secondary"].default
              }
              bgcolor={
                isDarkModeEnabled
                  ? "#333333"
                  : colorTokens.colors["action.primary"].default
              }
              border={
                isDarkModeEnabled
                  ? "#333333"
                  : colorTokens.colors["border.primary"].default
              }
              onClick={onClickOpenGiftDrawer}
            >
              <GiftIcon />
            </StreamingSendCommentButton>
          )}
        </Box>
      );
    }
  }
};

const StreamingSendCommentButton: FC<{
  children: React.ReactNode | string;
  color: string;
  bgcolor: string;
  border: string;
  disabled?: boolean;
  onClick: () => void | Promise<void>;
}> = ({ children, color, bgcolor, border, disabled = false, onClick }) => {
  const { t } = useI18n();

  return (
    <button
      aria-label={t("streaming.comment.sendAriaLabel")}
      disabled={disabled}
      css={css`
        display: flex;
        align-items: center;
        padding: 7px 10px;
        color: ${color};
        font-size: 12px;
        background-color: ${bgcolor};
        border-radius: 6px;
        border: 1px solid ${border};
        cursor: pointer;
        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const GiftIcon = () => {
  return (
    <SvgIcon
      viewBox="0 0 14 13"
      width={"14px"}
      height={"13px"}
      css={css`
        font-size: 16px !important;
      `}
    >
      <g transform="translate(0,0)">
        <path d="M12.3335 2.99998H10.8802C10.9535 2.79332 11.0002 2.56665 11.0002 2.33332C11.0009 1.9032 10.8627 1.48434 10.6063 1.13905C10.3498 0.793752 9.98874 0.54047 9.57676 0.416877C9.16478 0.293283 8.72392 0.305982 8.31973 0.453085C7.91555 0.600187 7.56966 0.873833 7.3335 1.23332L7.00016 1.67998L6.66683 1.22665C6.30683 0.693318 5.70016 0.333318 5.00016 0.333318C3.8935 0.333318 3.00016 1.22665 3.00016 2.33332C3.00016 2.56665 3.04683 2.79332 3.12016 2.99998H1.66683C0.926829 2.99998 0.340163 3.59332 0.340163 4.33332L0.333496 11.6667C0.333496 12.4067 0.926829 13 1.66683 13H12.3335C13.0735 13 13.6668 12.4067 13.6668 11.6667V4.33332C13.6668 3.59332 13.0735 2.99998 12.3335 2.99998ZM9.00016 1.66665C9.36683 1.66665 9.66683 1.96665 9.66683 2.33332C9.66683 2.69998 9.36683 2.99998 9.00016 2.99998C8.6335 2.99998 8.3335 2.69998 8.3335 2.33332C8.3335 1.96665 8.6335 1.66665 9.00016 1.66665ZM5.00016 1.66665C5.36683 1.66665 5.66683 1.96665 5.66683 2.33332C5.66683 2.69998 5.36683 2.99998 5.00016 2.99998C4.6335 2.99998 4.3335 2.69998 4.3335 2.33332C4.3335 1.96665 4.6335 1.66665 5.00016 1.66665ZM12.3335 11.6667H1.66683V10.3333H12.3335V11.6667ZM12.3335 8.33332H1.66683V4.33332H5.0535L3.66683 6.21998L4.74683 6.99998L6.3335 4.83998L7.00016 3.93332L7.66683 4.83998L9.2535 6.99998L10.3335 6.21998L8.94683 4.33332H12.3335V8.33332Z" />
      </g>
    </SvgIcon>
  );
};

const SendIcon = () => {
  return (
    <SvgIcon
      viewBox="0 0 15 12"
      width={"15px"}
      height={"12px"}
      css={css`
        font-size: 16px !important;
      `}
    >
      <g transform="translate(0,0)">
        <path d="M0.340163 12L14.3335 6L0.340163 0L0.333496 4.66667L10.3335 6L0.333496 7.33333L0.340163 12Z" />
      </g>
    </SvgIcon>
  );
};
